import React, { useEffect, useState } from "react";
import {
  clearOutlineCredentials,
  getSyncSettings,
  setOutlineCredentials,
  SyncSettings,
} from "../utils/api";
import "./SyncSettingsPanel.css";

/**
 * Each account connects its own Outline. The token is write-only: the API
 * returns whether one is stored, never the value.
 */
const OutlineConnectionPanel: React.FC = () => {
  const [settings, setSettings] = useState<SyncSettings | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const loaded = await getSyncSettings();
      setSettings(loaded);
      setBaseUrl(loaded.outline.baseUrl ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSave = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await setOutlineCredentials(baseUrl.trim(), apiToken.trim());
      // Never keep the token in component state once it has been sent.
      setApiToken("");
      setMessage(
        result.ready
          ? "Connected."
          : `Saved, but not ready yet: ${result.reason ?? "pick a collection below"}`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await clearOutlineCredentials();
      setBaseUrl("");
      setApiToken("");
      setMessage("Disconnected.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const connected = settings?.outline.connected ?? false;

  return (
    <section className="app-section">
      <div className="app-card sync-settings-panel">
        <h2 className="app-title">Outline connection</h2>

        <p className="sync-settings-status">
          {connected
            ? settings?.outline.ready
              ? "Connected and ready."
              : `Connected, not ready: ${settings?.outline.reason ?? "unknown reason"}`
            : "No Outline connected. Processed links are kept here until you connect one."}
        </p>

        <div className="sync-settings-group">
          <label htmlFor="outline-base-url">Outline URL</label>
          <input
            id="outline-base-url"
            type="url"
            placeholder="https://outline.example.com"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            disabled={busy}
          />
        </div>

        <div className="sync-settings-group">
          <label htmlFor="outline-token">API token</label>
          <input
            id="outline-token"
            type="password"
            autoComplete="off"
            placeholder={connected ? "Stored — enter a new token to replace it" : "ol_api_…"}
            value={apiToken}
            onChange={(event) => setApiToken(event.target.value)}
            disabled={busy}
          />
        </div>

        <div className="sync-settings-inline">
          <button
            type="button"
            className="app-button"
            onClick={() => void handleSave()}
            disabled={busy || !baseUrl.trim() || !apiToken.trim()}
          >
            {busy ? "Saving..." : connected ? "Update" : "Connect"}
          </button>
          {connected && (
            <button
              type="button"
              className="app-button secondary-button"
              onClick={() => void handleDisconnect()}
              disabled={busy}
            >
              Disconnect
            </button>
          )}
        </div>

        {message && <p className="sync-settings-status">{message}</p>}
        {error && <p className="sync-settings-error">{error}</p>}
      </div>
    </section>
  );
};

export default OutlineConnectionPanel;
